import * as React from "react";

import { getTokenUsageHeatmap } from "@/api/client";
import { TokenUsageHeatmapView } from "@/api/types";
import { cn } from "@/lib/utils";

const WEEKS = 26; // ~6 months
const DAY_MS = 86_400_000;

/** Five intensity buckets (0 = none), GitHub-style, scaled to the busiest day. */
function levelFor(tokens: number, max: number): number {
  if (tokens <= 0) return 0;
  const r = tokens / max;
  if (r > 0.75) return 4;
  if (r > 0.5) return 3;
  if (r > 0.25) return 2;
  return 1;
}

const LEVEL_CLASS = [
  "bg-muted",
  "bg-brand-blue/20",
  "bg-brand-blue/40",
  "bg-brand-blue/60",
  "bg-brand-blue",
];

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function startOfWeekSun(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() - out.getDay()); // back up to Sunday
  return out;
}

type Cell = { iso: string; date: Date; tokens: number; level: number; future: boolean };

/**
 * A GitHub-contributions-style heatmap of the user's LLM token usage over the last ~6 months.
 * Columns are weeks (Sun→Sat rows); cell intensity scales to the busiest day. Days with no usage
 * (and future days) render as the empty level, so the grid is always a full rectangle.
 */
export function TokenUsageHeatmapPanel() {
  const [data, setData] = React.useState<TokenUsageHeatmapView | null>(null);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    getTokenUsageHeatmap(WEEKS * 7)
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const { columns, monthLabels, busiest } = React.useMemo(() => {
    const byDate = new Map<string, number>();
    for (const p of data?.days ?? []) byDate.set(p.date, p.tokens);
    const max = Math.max(1, ...(byDate.size ? [...byDate.values()] : [1]));
    let busiestDay = 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const gridStart = startOfWeekSun(new Date(today.getTime() - (WEEKS * 7 - 1) * DAY_MS));

    const cols: Cell[][] = [];
    const labels: { col: number; text: string }[] = [];
    let cur = new Date(gridStart);
    let prevMonth = -1;
    for (let w = 0; w <= WEEKS; w++) {
      const col: Cell[] = [];
      for (let d = 0; d < 7; d++) {
        const iso = isoDay(cur);
        const tokens = byDate.get(iso) ?? 0;
        const future = cur.getTime() > today.getTime();
        if (!future) busiestDay = Math.max(busiestDay, tokens);
        col.push({ iso, date: new Date(cur), tokens, level: future ? 0 : levelFor(tokens, max), future });
        if (d === 0) {
          const m = cur.getMonth();
          if (m !== prevMonth) {
            labels.push({ col: w, text: cur.toLocaleDateString(undefined, { month: "short" }) });
            prevMonth = m;
          }
        }
        cur = new Date(cur.getTime() + DAY_MS);
      }
      cols.push(col);
      if (cur.getTime() > today.getTime()) break;
    }
    return { columns: cols, monthLabels: labels, busiest: busiestDay };
  }, [data]);

  return (
    <section className="comic-shadow-sm rounded-2xl border-2 border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-comic-headline text-sm uppercase tracking-wide">Token usage</h2>
        <span className="text-xs text-muted-foreground">
          {data ? (
            <>
              <span className="font-semibold text-foreground">{data.total.toLocaleString()}</span> tokens
              in the last 6 months
            </>
          ) : error ? (
            "Couldn't load usage."
          ) : (
            "Loading…"
          )}
        </span>
      </div>

      {!error && (
        <div className="overflow-x-auto">
          <div className="inline-flex flex-col gap-1">
            {/* Month labels */}
            <div className="flex gap-[3px] pl-8 text-[10px] text-muted-foreground">
              {columns.map((_, w) => {
                const label = monthLabels.find((m) => m.col === w);
                return (
                  <div key={w} className="w-[11px] shrink-0">
                    {label ? <span className="relative -left-px">{label.text}</span> : ""}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              {/* Weekday labels */}
              <div className="flex w-6 shrink-0 flex-col gap-[3px] pt-[0px] text-[9px] text-muted-foreground">
                {["", "Mon", "", "Wed", "", "Fri", ""].map((d, i) => (
                  <div key={i} className="h-[11px] leading-[11px]">
                    {d}
                  </div>
                ))}
              </div>
              {/* Week columns */}
              <div className="flex gap-[3px]">
                {columns.map((col, w) => (
                  <div key={w} className="flex flex-col gap-[3px]">
                    {col.map((cell) => (
                      <div
                        key={cell.iso}
                        title={
                          cell.future
                            ? ""
                            : `${cell.tokens.toLocaleString()} token${cell.tokens === 1 ? "" : "s"} · ${cell.date.toLocaleDateString(
                                undefined,
                                { weekday: "short", day: "numeric", month: "short" },
                              )}`
                        }
                        className={cn(
                          "h-[11px] w-[11px] rounded-[2px]",
                          cell.future ? "bg-transparent" : LEVEL_CLASS[cell.level],
                        )}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
        <span>{busiest > 0 ? `Busiest day: ${busiest.toLocaleString()} tokens` : "No usage yet"}</span>
        <span className="flex items-center gap-1">
          Less
          {LEVEL_CLASS.map((c, i) => (
            <span key={i} className={cn("h-[11px] w-[11px] rounded-[2px]", c)} />
          ))}
          More
        </span>
      </div>
    </section>
  );
}
